import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { EffectComposer, RenderPass, EffectPass, BloomEffect, SMAAEffect, SMAAPreset, KernelSize } from 'postprocessing';
import { N8AOPostPass } from 'n8ao';
import { QUALITY } from './quality';
import { sky, skyU } from './skyState';
import { AtmosphereEffect, GradeEffect } from './effects';
import { beatBridge } from '../state/beatBridge';
import { heliBridge } from '../city/heliTour';

// ── THE CAMERA'S SIDE OF THE PICTURE ─────────────────────────────────────────
//
//   scene → [ambient occlusion] → haze → bloom → tone curve + grade → [SMAA]
//
// Mounted whenever the new look is on (PlanetScene decides), and it takes over
// rendering by registering at priority 1 — r3f stops drawing on its own the
// moment any frame callback has a priority. That is the same mechanism
// StereoRender uses, so the two are never mounted together.
//
// `bypass`: inside a room the frame is drawn directly, exactly as before. Every
// interior's look was tuned under the renderer's own ACES with no bloom and no
// grade — per-room values are not ours to move — so the composer stands aside
// rather than re-grading them. It stays MOUNTED while it stands aside: tearing
// it down at every door and building it again at the next would recompile its
// passes on the threshold, which is exactly the hitch a door must not have.
//
// Ambient occlusion is the one effect that costs real GPU time, so it is the one
// the tiers move: half-res on `high`, full-res on `ultra`, absent on phones.

// ?postoff=1 — draw the street straight to the screen with the composer standing
// by, for isolating whether something is the pass's doing or the scene's.
const POST_OFF = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('postoff') === '1';

export default function PostFX({ bypass: bypassProp = false }) {
  const bypass = bypassProp || POST_OFF;
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const dpr = useThree((s) => s.viewport.dpr);

  const fx = useMemo(() => {
    const composer = new EffectComposer(gl, {
      frameBufferType: THREE.HalfFloatType,
      stencilBuffer: true,   // GroundPits cuts the stations' holes with it
      depthBuffer: true,
      multisampling: 0,
    });
    composer.addPass(new RenderPass(scene, camera));

    let ao = null;
    if (QUALITY.aoOn) {
      ao = new N8AOPostPass(scene, camera, size.width, size.height);
      ao.setQualityMode(QUALITY.ao >= 2 ? 'High' : 'Medium');
      ao.configuration.halfRes = QUALITY.aoHalf;
      ao.configuration.aoRadius = 1.6;
      ao.configuration.distanceFalloff = 1.0;
      ao.configuration.intensity = 2.6;
      ao.configuration.color = new THREE.Color('#141a2a');
      ao.configuration.gammaCorrection = false;
      // Off: "transparency aware" re-renders the WHOLE scene twice more every
      // frame (transparent-only, with everything else hidden) — and each of
      // those renders re-runs the shadow pass and wipes the shadow map. The few
      // transparent things on the street (glass, glows, labels) don't need AO.
      ao.configuration.transparencyAware = false;
      composer.addPass(ao);
    }

    const atmosphere = new AtmosphereEffect();
    const bloom = new BloomEffect({
      mipmapBlur: true,
      intensity: 0.6,
      luminanceThreshold: 0.9,
      luminanceSmoothing: 0.3,
      radius: 0.72,
      levels: 7,
      resolutionScale: QUALITY.bloomRes,
      kernelSize: KernelSize.LARGE,
    });
    const grade = new GradeEffect();
    const effects = [];
    if (QUALITY.hazeOn) effects.push(atmosphere);
    if (QUALITY.bloomOn) effects.push(bloom);
    effects.push(grade);
    composer.addPass(new EffectPass(camera, ...effects));
    if (QUALITY.smaa) composer.addPass(new EffectPass(camera, new SMAAEffect({ preset: SMAAPreset.HIGH })));
    return { composer, ao, atmosphere, bloom, grade };
    // The composer's passes hold the camera — a new default camera needs a new chain.
  }, [gl, scene, camera]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => fx.composer.dispose(), [fx]);

  // Size follows the canvas AND the pixel ratio (AdaptiveResolution moves dpr).
  useEffect(() => {
    fx.composer.setSize(size.width, size.height, false);
  }, [fx, size.width, size.height, dpr]);

  useFrame((state, dt) => {
    // ── DEPTH WRITES BACK ON BEFORE ANYTHING RENDERS ────────────────────────
    // Insurance, not the shadow fix (that was receiveShadow consistency — see
    // WorldLight's sweep). three's shadow pass clears each map with
    // renderer.clear(), and gl.clear only clears depth while the depth WRITE
    // MASK is on, which the shadow pass never sets; every fullscreen pass of
    // this composer ends with depth writes off. One cached state call makes the
    // next frame start from a known mask.
    gl.state.buffers.depth.setMask(true);
    if (bypass) {
      gl.render(scene, camera);
      return;
    }
    // A touch of the song in the light: at night, bloom swells on the beat —
    // the city breathes with the radio. Off by day, when nothing is lit.
    // Not from a helicopter (Khari, 2026-11-02: "make sure the beat/pulse … isn't
    // active when flying"): a whole-picture flash on every beat, seen from a seat
    // over the lit city, reads as the ride itself throbbing.
    const pulse = sky.night > 0.2 && !heliBridge.aboard ? (beatBridge.energy ?? 0.5) * Math.pow(Math.max(0, Math.cos((beatBridge.phase || 0) * Math.PI * 2)), 6) * 0.22 * sky.night : 0;
    fx.bloom.intensity = sky.bloom * (1 + pulse);
    fx.bloom.luminanceMaterial.threshold = sky.threshold;
    fx.atmosphere.sync(camera, sky, skyU);
    fx.grade.sync(sky, state.clock.elapsedTime, QUALITY.gradeOn);
    fx.composer.render(dt);
  }, 1);

  return null;
}
